import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../localization/useAppTranslation";
import { preferencesService } from "../services/preferencesService";
import type { TourProgress } from "../types/preferences";
import { COACH_TOURS, getCoachTour, getTourVariant } from "./registry";
import { TourRunner } from "./tourRunner";
import type {
  CoachTourDefinition,
  DesktopTourController,
  TourSidebarState,
  TourVariant,
} from "./types";

interface UseCoachToursResult {
  tourProgress: TourProgress[] | null;
  isLoading: boolean;
  loadError: string | null;
  completionError: string | null;
  loadTourProgress: () => Promise<TourProgress[] | null>;
  startTour: (tourId: string) => void;
}

const getErrorMessage = (error: unknown, fallbackMessage: string): string =>
  error instanceof Error ? error.message : fallbackMessage;

export const useCoachTours = (
  sessionId: string | null,
  isEnabled: boolean,
  isMobile: boolean,
  desktopController?: DesktopTourController,
  tourDefinitions: readonly CoachTourDefinition[] = COACH_TOURS,
): UseCoachToursResult => {
  const { t } = useTranslation("tour");
  const runnerReference = useRef<TourRunner | null>(null);
  const activeTourVariantReference = useRef<TourVariant | null>(null);
  const activeTourReference = useRef(false);
  const sidebarSnapshotReference = useRef<TourSidebarState | null>(null);
  const [tourProgress, setTourProgress] = useState<TourProgress[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [completionError, setCompletionError] = useState<string | null>(null);

  if (runnerReference.current === null) {
    runnerReference.current = new TourRunner();
  }

  const loadTourProgress = async (): Promise<TourProgress[] | null> => {
    if (!sessionId) {
      setTourProgress(null);
      setLoadError(null);
      return null;
    }

    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await preferencesService.listTourProgress(sessionId);
      setTourProgress(response.tours);
      return response.tours;
    } catch (error) {
      setTourProgress(null);
      setLoadError(getErrorMessage(error, t("errors.loadFailed")));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    void loadTourProgress();
  }, [isEnabled, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      runnerReference.current?.destroy();
    },
    [],
  );

  useEffect(() => {
    const currentVariant = getTourVariant(isMobile);
    if (
      activeTourVariantReference.current !== null &&
      activeTourVariantReference.current !== currentVariant
    ) {
      runnerReference.current?.destroy();
    }
  }, [isMobile]);

  const startTour = (tourId: string): void => {
    const tour = getCoachTour(tourId, tourDefinitions);
    const variant = getTourVariant(isMobile);
    if (!sessionId || !tour || !tour.variants.includes(variant)) {
      return;
    }

    const sidebarSnapshot = sidebarSnapshotReference.current
      ?? desktopController?.sidebarState
      ?? null;
    if (activeTourReference.current) {
      runnerReference.current?.destroy();
    }

    sidebarSnapshotReference.current = sidebarSnapshot;
    setCompletionError(null);
    const lifecycleContext = { variant, controller: desktopController };
    tour.onStart?.(lifecycleContext);
    runnerReference.current?.start({
      tour,
      variant,
      copy: {
        title: t(tour.titleKey),
        description: t(tour.popoverDescriptionKey),
        nextButton: t("popover.next"),
        backButton: t("popover.back"),
        doneButton: t("popover.done"),
        closeButton: t("popover.close"),
        progressText: t("popover.progress", {
          current: "{{current}}",
          total: "{{total}}",
        }),
      },
      steps: tour.createSteps({
        ...lifecycleContext,
        translate: (key) => t(key),
      }),
      onComplete: async () => {
        try {
          const savedProgress = await preferencesService.completeTour(
            sessionId,
            tour.id,
            tour.version,
          );
          setTourProgress((currentProgress) => [
            ...(currentProgress || []).filter(
              (progress) => progress.tour_id !== tour.id,
            ),
            savedProgress,
          ]);
        } catch (error) {
          setCompletionError(
            getErrorMessage(error, t("errors.completionFailed")),
          );
        }
      },
      onCleanup: () => {
        activeTourReference.current = false;
        activeTourVariantReference.current = null;
        tour.onCleanup?.(lifecycleContext);
        const originalSidebarState = sidebarSnapshotReference.current;
        sidebarSnapshotReference.current = null;
        if (originalSidebarState) {
          desktopController?.restoreSidebar(originalSidebarState);
        }
      },
    });
    activeTourReference.current = true;
    activeTourVariantReference.current = variant;
  };

  return {
    tourProgress,
    isLoading,
    loadError,
    completionError,
    loadTourProgress,
    startTour,
  };
};
